import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { PROGRAM, getSectionById } from '../data/newCurriculum';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, Lock, Check, Play, BookOpen, Mic, MessageSquare, FileText, Video, Award } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const lessonTypeIcons = {
  flashcard: BookOpen,
  voice: Mic,
  roleplay: MessageSquare,
  quiz: FileText,
  video: Video,
  exam: Award
};

const lessonTypeLabels = {
  flashcard: 'Kelime Kartları',
  voice: 'Ses Pratiği',
  roleplay: 'Rol Yapma',
  quiz: 'Quiz',
  video: 'Video',
  exam: 'Sınav'
};

export default function ProgramSection() {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedUnit, setExpandedUnit] = useState(null);

  const section = getSectionById(sectionId);

  useEffect(() => {
    if (!section) {
      navigate('/dashboard');
      return;
    }
    fetchProgress();
  }, [sectionId]);

  const fetchProgress = async () => {
    try {
      const res = await axios.get(`${API}/program/section-progress/${sectionId}`, { withCredentials: true });
      setProgress(res.data);
    } catch (error) {
      console.error('İlerleme yüklenemedi:', error);
      setProgress({ completedLessons: [], completedUnits: [] });
    } finally {
      setLoading(false);
    }
  };

  const isLessonCompleted = (unitId, lessonId) => {
    const key = `${unitId}-${lessonId}`;
    return progress.completedLessons?.includes(key) || false;
  };

  const isUnitCompleted = (unitId) => {
    return progress.completedUnits?.includes(unitId) || false;
  };

  const getUnitProgress = (unit) => {
    const completed = unit.lessons.filter(l => isLessonCompleted(unit.id, l.id)).length;
    return Math.round((completed / unit.lessons.length) * 100);
  };

  const isUnitLocked = (unitIndex) => {
    // TEST MODE: Tüm üniteler açık
    return false;
    
    /* PRODUCTION MODE:
    if (unitIndex === 0) return false;
    // Önceki ünite tamamlanmalı
    const prevUnit = section.units[unitIndex - 1];
    return !isUnitCompleted(prevUnit.id);
    */
  };

  const handleLessonClick = (unit, lesson) => {
    navigate(`/program/${sectionId}/lesson/${unit.id}/${lesson.id}`, {
      state: { unit, lesson, section }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!section) return null;

  const totalLessons = section.units.reduce((sum, u) => sum + u.lessons.length, 0);
  const completedLessons = progress.completedLessons?.length || 0;
  const sectionProgress = Math.round((completedLessons / totalLessons) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <header className={`bg-gradient-to-r ${section.color} text-white`}>
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-white/80 hover:text-white hover:bg-white/10 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Ana Sayfa
          </Button>
          
          <div className="flex items-center gap-4">
            <span className="text-5xl">{section.icon}</span>
            <div className="flex-1">
              <p className="text-white/70 text-sm font-medium">BÖLÜM {section.order}</p>
              <h1 className="text-2xl font-bold mb-1">{section.title}</h1>
              <p className="text-white/80">{section.subtitle}</p>
            </div>
          </div>
          
          {/* Progress */}
          <div className="mt-6 bg-white/20 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Bölüm İlerlemesi</span>
              <span className="text-sm font-bold">{sectionProgress}%</span>
            </div>
            <Progress value={sectionProgress} className="h-2 bg-white/30" />
            <p className="text-xs text-white/70 mt-2">
              {completedLessons} / {totalLessons} ders tamamlandı
            </p>
          </div>
        </div>
      </header>

      {/* Units List */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="space-y-4">
          {section.units.map((unit, index) => {
            const locked = isUnitLocked(index);
            const completed = isUnitCompleted(unit.id);
            const unitProgress = getUnitProgress(unit);
            const isExpanded = expandedUnit === unit.id;
            
            return (
              <Card 
                key={unit.id}
                className={`overflow-hidden transition-all duration-300 ${
                  locked ? 'opacity-60' : completed ? 'border-green-300 bg-green-50/50' : ''
                }`}
              >
                <CardContent className="p-0">
                  {/* Unit Header */}
                  <div 
                    className={`p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      locked ? 'cursor-not-allowed' : ''
                    }`}
                    onClick={() => !locked && setExpandedUnit(isExpanded ? null : unit.id)}
                  >
                    {/* Unit Status Icon */}
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                      completed ? 'bg-green-100' : locked ? 'bg-gray-100' : 'bg-blue-100'
                    }`}>
                      {locked ? (
                        <Lock className="h-5 w-5 text-gray-400" />
                      ) : completed ? (
                        <Check className="h-6 w-6 text-green-600" />
                      ) : (
                        unit.icon
                      )}
                    </div>
                    
                    {/* Unit Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-400">ÜNİTE {unit.id}</span>
                        {completed && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            Tamamlandı
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-800">{unit.title}</h3>
                      <p className="text-sm text-gray-500">{unit.lessons.length} ders</p>
                    </div>
                    
                    {/* Progress Circle */}
                    {!locked && (
                      <div className="text-center">
                        <div className={`w-12 h-12 rounded-full border-4 flex items-center justify-center ${
                          completed ? 'border-green-500' : 'border-blue-200'
                        }`}>
                          <span className={`text-sm font-bold ${
                            completed ? 'text-green-600' : 'text-gray-600'
                          }`}>
                            {unitProgress}%
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Lessons List (Expanded) */}
                  {isExpanded && !locked && (
                    <div className="border-t bg-gray-50 p-4">
                      <div className="space-y-2">
                        {unit.lessons.map((lesson, lessonIndex) => {
                          const lessonCompleted = isLessonCompleted(unit.id, lesson.id);
                          const LessonIcon = lessonTypeIcons[lesson.type] || BookOpen;
                          
                          return (
                            <div
                              key={lesson.id}
                              className={`flex items-center gap-3 p-3 rounded-lg bg-white border cursor-pointer transition-all hover:shadow-md ${
                                lessonCompleted ? 'border-green-200' : 'border-gray-200 hover:border-blue-300'
                              }`}
                              onClick={() => handleLessonClick(unit, lesson)}
                            >
                              {/* Lesson Number/Status */}
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                                lessonCompleted 
                                  ? 'bg-green-100 text-green-600' 
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {lessonCompleted ? (
                                  <Check className="h-4 w-4" />
                                ) : (
                                  lessonIndex + 1
                                )}
                              </div>
                              
                              {/* Lesson Info */}
                              <div className="flex-1">
                                <h4 className="font-medium text-gray-800 text-sm">
                                  {lesson.title}
                                </h4>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  <LessonIcon className="h-3 w-3" />
                                  <span>{lessonTypeLabels[lesson.type]}</span>
                                  <span>•</span>
                                  <span>{lesson.duration} dk</span>
                                </div>
                              </div>
                              
                              {/* Play Button */}
                              {!lessonCompleted && (
                                <Button size="sm" variant="ghost" className="text-blue-600">
                                  <Play className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
}
